import {
  type WebsiteMarketingTagListItem,
  type WebsiteMarketingTagListQuery,
} from '@utazzo/shared';
import { UzBadge } from '@utazzo/ui';
import React from 'react';
import { useSearchParams } from 'react-router-dom';

import { MarketingTagFormDrawer } from '../components/MarketingTagFormDrawer';
import { listMarketingTags } from '../marketing-tags.api';
import {
  useBulkDeleteMarketingTags,
  useDeleteMarketingTag,
  useMarketingTagsList,
} from '../use-marketing-tags';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

type TagType = 'theme' | 'collection';

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'theme', label: 'Themes' },
  { value: 'collection', label: 'Collections' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
] as const;

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'type',
    label: 'Type',
    options: TYPE_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'isActive',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
];

export const MarketingTagsListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN' || me?.role === 'OPS';
  const deleteTag = useDeleteMarketingTag();
  const bulkDeleteMut = useBulkDeleteMarketingTags();

  // Seed the create form's type from the active list filter (`?f.type=`), which
  // the DataGrid also stores in the URL.
  const [searchParams] = useSearchParams();
  const activeType = searchParams.get('f.type');
  const defaultType: TagType = activeType === 'collection' ? 'collection' : 'theme';

  const columns: ListScreenColumns<WebsiteMarketingTagListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<WebsiteMarketingTagListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => <span className="font-medium text-zinc-900">{row.name}</span>,
      },
      {
        id: 'type',
        header: 'Type',
        cell: (row) => <UzBadge tone="secondary">{row.type}</UzBadge>,
      },
      {
        id: 'isActive',
        header: 'Status',
        cell: (row) => (
          <UzBadge tone={row.isActive ? 'success' : 'secondary'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </UzBadge>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-500">{row.description ?? '-'}</span>,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<WebsiteMarketingTagListItem, WebsiteMarketingTagListQuery>
      title="Themes & Collections"
      description="Themes & collections that power the public website's curated landing pages."
      useList={useMarketingTagsList}
      defaultSort={{ by: 'name', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const type = state.filters.type;
        const isActive = state.filters.isActive;
        return {
          ...base,
          ...(type ? { type } : {}),
          ...(isActive ? { isActive: isActive === 'true' } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add tag' }}
      renderDrawer={({ open, mode, row, close }) => (
        <MarketingTagFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          tag={row}
          defaultType={defaultType}
        />
      )}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} tag${rows.length === 1 ? '' : 's'}?`,
            description: 'These marketing tags will be soft-deleted and hidden from the website.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listMarketingTags(q),
        filename: 'marketing-tags',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Type', value: (r) => r.type },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Description', value: (r) => r.description ?? '' },
        ],
      }}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteTag.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this tag?',
            description: `${row.name} will be soft-deleted and hidden from the website.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name (min 3 characters)',
        emptyTitle: 'No themes or collections match these filters',
        emptyDescription: 'Adjust the type filter or clear the search box.',
        errorTitle: 'Failed to load themes & collections',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
